import React, { useState, useEffect, useMemo, FunctionComponent } from 'react'
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { SliderButton } from '@typeform/embed-react'
import {
  IconArrow,
  IconSpotify,
  WarningNoDataIcon,
} from 'Stories/Icon/Icon.stories'
import { Mobile, Desktop } from 'Shared/components/Responsive'
import { numberFormatSpaces } from 'Constants/functions'
import {
  getCampaignDetails,
  setSpotifyConnectionForArtist,
} from 'Redux/actions/campaignActions'
import { getUserSelector } from 'Redux/selectors/auth'
import { locationEvent, pageInfo, pageViewEvent } from 'Helpers/tagManager'
import { Review, ReviewStatus } from '../../Types'
import CampaignRating from './components/CampaignRating'
import ProgressBar from './components/ProgressBar'
import CampaignGenres from './components/CampaignGenres'
import CampaignLanguages from './components/CampaignLanguages'
import SpotifyForArtistModal from '../ui/SpotifyForArtistModal'

import {
  CampaignDetailsData,
  // ReviewStatus,
  CampaignStatus,
  Tabs,
  CampaignResultsProps,
} from './props'
import {
  BackButton,
  CampaignCalculation,
  CampaignCalculationLabel,
  CampaignCalculationValue,
  CampaignCalculationsContainer,
  Card,
  CardAdditionalInfo,
  CardBody,
  CardMainValue,
  CardTitle,
  CardsContainer,
  CardsOverflow,
  ConnectSFA,
  ConnectedSFA,
  ConnectedSFAContainer,
  Container,
  DataNotReceivedContainer,
  DataNotReceivedLabel,
  DotConnectedSFA,
  GenresAndLanguagesContainer,
  PageTitle,
  PageTitleContainer,
  ReviewsContainer,
  SpotifyIconContainer,
  TabButton,
  TabButtonCount,
  TabTitles,
  ToggleDetails,
  TrackAuthor,
  TrackAvatar,
  TrackAvatarMobile,
  TrackInfoColumnMobile,
  TrackInfoContainer,
  TrackInfoContainerMobile,
  TrackInfoLabelMobile,
  TrackInfoRowMobile,
  TrackInfoTextMobile,
  TrackTitle,
  TrackTitleContainer,
  DataNotReceivedIcon,
} from './styles'

const CampaignResults: FunctionComponent<CampaignResultsProps> = ({
  match,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const user = useSelector(getUserSelector)
  const campaignDetails: CampaignDetailsData = useSelector(
    (state: RootStateOrAny) => state.campaignDetails.details
  )

  const { t } = useTranslation()

  useEffect(() => {
    if (user) {
      pageViewEvent(user, pageInfo.campaignResults)
      locationEvent()
    }
  }, [user])

  const campaignID = +match.params.campaignID

  const [active, setActiveLink] = useState<Tabs>(Tabs.all)
  const [filteredReviews, setFilteredReviews] = useState<Review[]>([])
  const [modal, setModal] = useState<boolean>(false)
  const [showDetailsMobile, setShowDetailsMobile] = useState<boolean>(false)
  const [typeformHasBeenOpened, setTypeformHasBeenOpened] = useState<
    'load' | 'exit'
  >('load')

  const toggleDetailsMobile = () => setShowDetailsMobile(!showDetailsMobile)

  useEffect(() => {
    dispatch(getCampaignDetails(campaignID))
  }, [campaignID])

  useEffect(() => {
    campaignDetails.Reviews && setFilteredReviews(campaignDetails.Reviews)
  }, [campaignDetails])

  const modalToggle = () => setModal(!modal)
  const openConnectSFAModal = () => setModal(true)

  const memoReviewedReviews = useMemo(
    () =>
      campaignDetails.Reviews
        ? campaignDetails.Reviews.filter(
            i => i.review_status === ReviewStatus.reviewed
          )
        : [],
    [campaignDetails]
  )

  const memoAddedReviews = useMemo(
    () =>
      campaignDetails.Reviews
        ? campaignDetails.Reviews.filter(i => i.playlist_id)
        : [],
    [campaignDetails]
  )

  const memoMissedReviews = useMemo(
    () =>
      campaignDetails.Reviews
        ? campaignDetails.Reviews.filter(
            i => i.review_status !== ReviewStatus.reviewed
          )
        : [],
    [campaignDetails]
  )

  const toggle = (str: Tabs) => {
    setActiveLink(str)

    switch (str) {
      case Tabs.all:
        setFilteredReviews(campaignDetails.Reviews)
        break
      case Tabs.added:
        setFilteredReviews(memoAddedReviews)
        break
      case Tabs.reviewed:
        setFilteredReviews(memoReviewedReviews)
        break
      case Tabs.missed:
        setFilteredReviews(memoMissedReviews)
        break
      default:
        setFilteredReviews(campaignDetails.Reviews)
    }
  }

  const getPopularityDifference = () => {
    const spotifyPopularity = campaignDetails.Track.spotify_popularity
    const spotifyPopularityResult =
      campaignDetails.Track.spotify_popularity_result

    if (spotifyPopularityResult > spotifyPopularity) {
      return spotifyPopularityResult - spotifyPopularity
    }
    return 0
  }

  const handleGoBack = () => {
    history.go(-1)
    localStorage.removeItem('alreadyClosed')
  }
  const removeOverflowHidden = () => {
    setTimeout(() => document.body.removeAttribute('style'), 200)
  }

  localStorage.setItem('typeformEndedCampaign', 'typeformEndedCampaign')
  const typeformEndedCampaign = localStorage.getItem('typeformEndedCampaign')
  const alreadyClosed = localStorage.getItem('alreadyClosed')

  const res = useMemo(() => {
    if (window.innerWidth > 576) {
      return 550
    }
    return 320
  }, [window.innerWidth])

  return (
    <Container>
      {typeformEndedCampaign &&
        !alreadyClosed &&
        campaignDetails?.status === CampaignStatus.ended && (
          // Typeform survey in the ended campaign
          <SliderButton
            hidden={{
              email: user?.email,
              campaign_id: `${campaignDetails?.id}`,
              campaign_type: `${campaignDetails?.type}`,
              campaign_name: campaignDetails?.Track?.track_name,
              is_feed: campaignDetails?.is_feed ? 'yes' : 'no',
            }}
            id="VP92h7z5"
            open={typeformHasBeenOpened}
            width={res}
            style={{
              visibility: 'hidden',
            }}
            onSubmit={() => {
              setTypeformHasBeenOpened('exit')
              localStorage.removeItem('typeformEndedCampaign')
              removeOverflowHidden()
            }}
            onReady={() => {
              localStorage.setItem('alreadyClosed', 'alreadyClosed')
            }}
          >
            {t('sidebar.shareFeedback')}
          </SliderButton>
        )}

      <SpotifyForArtistModal
        modal={modal}
        toggle={modalToggle}
        setSpotifyConnectionForArtist={setSpotifyConnectionForArtist}
        artistSpotifyId={
          !isEmpty(campaignDetails) && campaignDetails.Track.artist_spotify_id
        }
      />
      {!isEmpty(campaignDetails) && (
        <>
          <PageTitleContainer>
            <BackButton onClick={handleGoBack}>
              <IconArrow />
            </BackButton>
            <PageTitle>{t('campaignResultsPage.pageTitle')}</PageTitle>
          </PageTitleContainer>
          <Desktop>
            <TrackInfoContainer>
              <SpotifyIconContainer>
                <IconSpotify />
              </SpotifyIconContainer>
              <TrackAvatar
                src={campaignDetails.Track.track_image}
                alt={campaignDetails.Track.track_name}
              />
              <TrackTitleContainer>
                <TrackTitle>{campaignDetails.Track.track_name}</TrackTitle>
                <TrackAuthor>{campaignDetails.Track.artist_name}</TrackAuthor>
              </TrackTitleContainer>
              <CampaignCalculationsContainer>
                <CampaignCalculation>
                  <CampaignCalculationLabel>
                    {t('campaignResultsPage.budgetLabel')}:{' '}
                  </CampaignCalculationLabel>
                  <CampaignCalculationValue>{`$${campaignDetails.budget}`}</CampaignCalculationValue>
                </CampaignCalculation>
                <CampaignCalculation>
                  <CampaignCalculationLabel>
                    {t('campaignResultsPage.curatorsLabel')}:{' '}
                  </CampaignCalculationLabel>
                  <CampaignCalculationValue>
                    {campaignDetails.curators_count}
                    {campaignDetails.curators_count_max
                      ? `-${campaignDetails.curators_count_max}`
                      : ''}
                  </CampaignCalculationValue>
                </CampaignCalculation>
                <CampaignCalculation>
                  <CampaignCalculationLabel>
                    {t('campaignResultsPage.playlistsLabel')}:{' '}
                  </CampaignCalculationLabel>
                  <CampaignCalculationValue>
                    {campaignDetails.playlists_count}
                    {campaignDetails.playlists_count_max
                      ? `-${campaignDetails.playlists_count_max}`
                      : ''}
                  </CampaignCalculationValue>
                </CampaignCalculation>
                {campaignDetails.status === CampaignStatus.ended && (
                  <CampaignCalculation>
                    <CampaignCalculationLabel>
                      {t('campaignResultsPage.refundLabel')}:{' '}
                    </CampaignCalculationLabel>
                    <CampaignCalculationValue
                      green
                    >{`$${campaignDetails.refund}`}</CampaignCalculationValue>
                  </CampaignCalculation>
                )}
              </CampaignCalculationsContainer>
              <ProgressBar campaignDetails={campaignDetails} />
              <GenresAndLanguagesContainer>
                <CampaignGenres genres={campaignDetails.Track.TrackGenres} />
                <CampaignLanguages
                  languages={campaignDetails.Track.TrackLanguages}
                />
              </GenresAndLanguagesContainer>
            </TrackInfoContainer>
          </Desktop>
          <Mobile>
            <TrackInfoContainerMobile>
              <TrackAvatarMobile>
                <TrackAvatar
                  src={campaignDetails.Track.track_image}
                  alt={campaignDetails.Track.track_name}
                />
                <SpotifyIconContainer>
                  <IconSpotify />
                </SpotifyIconContainer>
              </TrackAvatarMobile>
              <TrackInfoColumnMobile>
                <TrackInfoRowMobile>
                  <TrackInfoLabelMobile>
                    {t('campaignResultsPage.trackName')}
                  </TrackInfoLabelMobile>
                  <TrackInfoTextMobile>
                    {campaignDetails.Track.track_name}
                  </TrackInfoTextMobile>
                </TrackInfoRowMobile>
                <TrackInfoRowMobile>
                  <TrackInfoLabelMobile>
                    {t('campaignResultsPage.artist')}
                  </TrackInfoLabelMobile>
                  <TrackInfoTextMobile>
                    {campaignDetails.Track.artist_name}
                  </TrackInfoTextMobile>
                </TrackInfoRowMobile>
                {showDetailsMobile && (
                  <>
                    <CampaignCalculationsContainer>
                      <CampaignCalculation>
                        <CampaignCalculationLabel>
                          {t('campaignResultsPage.budgetLabel')}:{' '}
                        </CampaignCalculationLabel>
                        <CampaignCalculationValue>{`$${campaignDetails.budget}`}</CampaignCalculationValue>
                      </CampaignCalculation>
                      <CampaignCalculation>
                        <CampaignCalculationLabel>
                          {t('campaignResultsPage.curatorsLabel')}:{' '}
                        </CampaignCalculationLabel>
                        <CampaignCalculationValue>
                          {campaignDetails.curators_count}
                          {campaignDetails.curators_count_max
                            ? `-${campaignDetails.curators_count_max}`
                            : ''}
                        </CampaignCalculationValue>
                      </CampaignCalculation>
                      <CampaignCalculation>
                        <CampaignCalculationLabel>
                          {t('campaignResultsPage.playlistsLabel')}:{' '}
                        </CampaignCalculationLabel>
                        <CampaignCalculationValue>
                          {campaignDetails.playlists_count}
                          {campaignDetails.playlists_count_max
                            ? `-${campaignDetails.playlists_count_max}`
                            : ''}
                        </CampaignCalculationValue>
                      </CampaignCalculation>
                      {campaignDetails.status === CampaignStatus.ended && (
                        <CampaignCalculation>
                          <CampaignCalculationLabel>
                            {t('campaignResultsPage.refundLabel')}:{' '}
                          </CampaignCalculationLabel>
                          <CampaignCalculationValue
                            green
                          >{`$${campaignDetails.refund}`}</CampaignCalculationValue>
                        </CampaignCalculation>
                      )}
                    </CampaignCalculationsContainer>
                    <ProgressBar campaignDetails={campaignDetails} />
                    <GenresAndLanguagesContainer>
                      <CampaignGenres
                        genres={campaignDetails.Track.TrackGenres}
                      />
                      <CampaignLanguages
                        languages={campaignDetails.Track.TrackLanguages}
                      />
                    </GenresAndLanguagesContainer>
                  </>
                )}
                <ToggleDetails onClick={toggleDetailsMobile}>
                  {showDetailsMobile
                    ? t('campaignResultsPage.hideDetails')
                    : t('campaignResultsPage.showDetails')}
                </ToggleDetails>
              </TrackInfoColumnMobile>
            </TrackInfoContainerMobile>
          </Mobile>
          <CardsOverflow>
            <CardsContainer>
              <Card>
                <CardBody>
                  <CardTitle>{t('campaignResultsPage.streams')}</CardTitle>
                  {!campaignDetails.is_sfa_connected ? (
                    <DataNotReceivedContainer>
                      <DataNotReceivedIcon>
                        <WarningNoDataIcon />
                      </DataNotReceivedIcon>
                      <DataNotReceivedLabel>
                        {t('campaignResultsPage.dataNotReceived')}
                      </DataNotReceivedLabel>
                    </DataNotReceivedContainer>
                  ) : (
                    <CardMainValue>
                      {numberFormatSpaces(campaignDetails.streams_count)}
                    </CardMainValue>
                  )}
                  <CardAdditionalInfo>
                    {!campaignDetails.is_sfa_connected ? (
                      <ConnectSFA onClick={openConnectSFAModal}>
                        {t('campaignResultsPage.connectSpotifyForArtists')}
                      </ConnectSFA>
                    ) : (
                      <ConnectedSFAContainer>
                        <DotConnectedSFA />
                        <ConnectedSFA>
                          {t('campaignResultsPage.connectedSFA')}
                        </ConnectedSFA>
                      </ConnectedSFAContainer>
                    )}
                  </CardAdditionalInfo>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <CardTitle>{t('campaignResultsPage.listeners')}</CardTitle>
                  {!campaignDetails.is_sfa_connected ? (
                    <DataNotReceivedContainer>
                      <DataNotReceivedIcon>
                        <WarningNoDataIcon />
                      </DataNotReceivedIcon>
                      <DataNotReceivedLabel>
                        {t('campaignResultsPage.dataNotReceived')}
                      </DataNotReceivedLabel>
                    </DataNotReceivedContainer>
                  ) : (
                    <CardMainValue>
                      {numberFormatSpaces(campaignDetails.listeners_count)}
                    </CardMainValue>
                  )}
                  <CardAdditionalInfo>
                    {!campaignDetails.is_sfa_connected ? (
                      <ConnectSFA onClick={openConnectSFAModal}>
                        {t('campaignResultsPage.connectSpotifyForArtists')}
                      </ConnectSFA>
                    ) : (
                      <ConnectedSFAContainer>
                        <DotConnectedSFA />
                        <ConnectedSFA>
                          {t('campaignResultsPage.connectedSFA')}
                        </ConnectedSFA>
                      </ConnectedSFAContainer>
                    )}
                  </CardAdditionalInfo>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <CardTitle>
                    {t('campaignResultsPage.playlistsAdds')}
                  </CardTitle>
                  <CardMainValue>
                    {numberFormatSpaces(
                      campaignDetails.playlists_followers_result
                    )}
                  </CardMainValue>
                  <CardAdditionalInfo>
                    +{campaignDetails.playlists_adds}{' '}
                    {t('campaignResultsPage.playlists')}
                  </CardAdditionalInfo>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <CardTitle>
                    {t('campaignResultsPage.spotifyPopularity')}
                  </CardTitle>
                  <CardMainValue>
                    {campaignDetails.Track.spotify_popularity_result
                      ? campaignDetails.Track.spotify_popularity_result
                      : campaignDetails.Track.spotify_popularity}
                  </CardMainValue>
                  <CardAdditionalInfo>
                    +{getPopularityDifference()}&ensp;
                    {t('campaignResultsPage.newFollowers')}
                  </CardAdditionalInfo>
                </CardBody>
              </Card>
            </CardsContainer>
          </CardsOverflow>
          <ReviewsContainer>
            <TabTitles>
              <TabButton
                type="button"
                active={active === Tabs.all}
                onClick={() => toggle(Tabs.all)}
              >
                {t('campaignResultsPage.all')}
                <TabButtonCount>
                  {campaignDetails.Reviews.length}
                </TabButtonCount>
              </TabButton>
              <TabButton
                type="button"
                active={active === Tabs.reviewed}
                onClick={() => toggle(Tabs.reviewed)}
              >
                {t('campaignResultsPage.reviewed')}
                <TabButtonCount>{memoReviewedReviews.length}</TabButtonCount>
              </TabButton>
              <TabButton
                type="button"
                active={active === Tabs.added}
                onClick={() => toggle(Tabs.added)}
              >
                {t('campaignResultsPage.added')}
                <TabButtonCount green>{memoAddedReviews.length}</TabButtonCount>
              </TabButton>
              <TabButton
                type="button"
                active={active === Tabs.missed}
                onClick={() => toggle(Tabs.missed)}
              >
                {t('campaignResultsPage.missed')}
                <TabButtonCount red>{memoMissedReviews.length}</TabButtonCount>
              </TabButton>
            </TabTitles>
            <div>
              {filteredReviews.map(review => (
                <CampaignRating
                  review={review}
                  key={review.id}
                  campaignID={campaignID}
                  openConnectSFAModal={openConnectSFAModal}
                  isSFAConnected={campaignDetails.is_sfa_connected}
                />
              ))}
            </div>
          </ReviewsContainer>
        </>
      )}
    </Container>
  )
}

export default CampaignResults
